/**
 * Theme — the single source of truth for light / dark / system.
 *
 * WHY THIS EXISTS: the header toggle (DashboardLayout) and the Appearance tab
 * (Settings) each used to own a local `useState` and poke
 * `documentElement.classList` themselves. Two controls, two disagreeing
 * pictures of the same class, and neither survived a reload. Theme is one piece
 * of state shared by the whole app, so it lives outside React and both surfaces
 * subscribe to it.
 *
 * The preference is stored as a bare string (not JSON) under a key the inline
 * boot script in `index.html` also reads, so the class is on <html> before the
 * first paint and there is no flash of the wrong theme. Keep the key and the
 * accepted values in sync with that script.
 */

export type ThemePreference = 'light' | 'dark' | 'system';

/** Also hardcoded in the boot script in index.html — change both together. */
export const THEME_STORAGE_KEY = 'tyashin-theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readStoredTheme(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(raw) ? raw : 'system';
  } catch {
    // Storage can throw in private-mode / blocked-cookie browsers. Falling back
    // to 'system' keeps the app usable instead of blanking on boot.
    return 'system';
  }
}

function prefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

/** True when the given preference should render dark right now. */
export function resolvesToDark(preference: ThemePreference): boolean {
  return preference === 'dark' || (preference === 'system' && prefersDark());
}

let current: ThemePreference = readStoredTheme();
const listeners = new Set<() => void>();

function applyToDocument(): void {
  document.documentElement.classList.toggle('dark', resolvesToDark(current));
}

function notify(): void {
  listeners.forEach((listener) => listener());
}

/** Current preference (`'system'` is a preference, not a resolved value). */
export function getTheme(): ThemePreference {
  return current;
}

/** Persist and apply a preference, then wake every subscribed component. */
export function setTheme(preference: ThemePreference): void {
  current = preference;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // A tab that cannot persist still gets the right theme for its lifetime.
  }
  applyToDocument();
  notify();
}

export function subscribeToTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Called once at startup. The boot script has already set the class; this
 * re-applies it (so a stale class from anywhere else is corrected) and keeps
 * the 'system' preference live when the OS flips between light and dark.
 */
export function initTheme(): void {
  applyToDocument();
  if (typeof window.matchMedia !== 'function') return;
  window.matchMedia(DARK_QUERY).addEventListener('change', () => {
    if (current !== 'system') return;
    applyToDocument();
    notify();
  });
}
